import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "mt-8 mb-5 flex flex-col" }
const _hoisted_2 = { class: "text-2xl text-light4 -mb-2 uppercase font-black truncate" }
const _hoisted_3 = { class: "text-6xl leading-tight truncate font-black" }
const _hoisted_4 = { class: "text-3xl" }
const _hoisted_5 = { class: "lowercase" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", null, " - ", -1)
const _hoisted_7 = { class: "lowercase" }
const _hoisted_8 = { class: "lowercase" }
const _hoisted_9 = { class: "text-light3" }
const _hoisted_10 = { class: "mb-12" }
const _hoisted_11 = { class: "flex" }
const _hoisted_12 = { class: "text-3xl" }
const _hoisted_13 = { class: "text-2xl mx-5" }

import ErrorModal from "@/components/Modal/ErrorModal.vue";
import BaseButton from "@/components/Button/BaseButton.vue";
import BUTTONTYPE from "@/components/Button/Button.types";
import ProgressBar from "@/components/ProgressBar/ProgressBar.vue";
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import {
  getNumberOfHours,
  getNumberOfMinutes,
  getTimeDiff,
  getNumberOfSeconds,
  getNumberOfDays,
  getMinutesWithoutHours,
  MINUTE,
} from "@/helpers/Time";
import { useProfileStore } from "@/store/Profile";
import { useScheduleStore } from "@/store/Schedule";
import EndMeetingModal from "@/components/Modal/EndMeetingModal.vue";
import { useReservation } from "@/composables/reservation";
import { storeToRefs } from "pinia";
import { addMinutes, subMinutes } from "date-fns";
import useSound from "@/composables/sound";
import loggingService from "@/services/LoggingService";
import { CreatedVia } from "@/types/Reservation";
import { useI18n } from "vue-i18n";
import i18n from "@/locale";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const showErrorModal = ref(false);
const endNowModal = ref(false);
const errorMessage = ref("");
const isEndReservationLoading = ref(false);
const isAddTimeLoading = ref(false);
const addTimeRequestInFlight = ref(false);
const addTimeClickCount = ref(0);

let addTimeTimer: number | null = null;

const profileStore = useProfileStore();
const scheduleStore = useScheduleStore();

const { firstFutureReservation } = storeToRefs(scheduleStore);

const { startTime, endTime, pastDays, futureDays, reservationTitle } = useReservation(firstFutureReservation);

const { playSound, soundPlayed } = useSound("ending");

onMounted(() => {
  loggingService.info("InProgress.vue", "🟥 In progress view mounted");
});

const barProgress = computed(() => {
  const passed = getNumberOfSeconds(getTimeDiff(firstFutureReservation.value.startTime, scheduleStore.getTime));
  const total = getNumberOfSeconds(
    getTimeDiff(firstFutureReservation.value.startTime, firstFutureReservation.value.endTime)
  );
  return (passed / total) * 100;
});

const timeLimit = computed(() => {
  return firstFutureReservation.value.createdVia === CreatedVia.DESKTOP
    ? profileStore.webTimeLimit
    : profileStore.tabletTimeLimit;
});

const addTimeButtonClass = computed(() => {
  return isAddTimeDisabled.value ? "ml-4 w-44 opacity-50" : "ml-4 w-44";
});

const isAddTimeDisabled = computed(() => {
  return isAddTimeLoading.value || minutesToAdd.value < 1 || addTimeRequestInFlight.value;
});

const { t } = useI18n();

const remaining = computed(() => {
  const timeDiff = getTimeDiff(firstFutureReservation.value.endTime, scheduleStore.getTime) + MINUTE;
  const diffHours = getNumberOfHours(timeDiff);
  const diffMinutes = getMinutesWithoutHours(timeDiff);

  const remainingHours = diffHours !== 0 ? t("remainingHours", { diffHours }) : "";
  const remainingMinutes = diffMinutes !== 0 ? t("remainingMinutesMin", { diffMinutes }) : "";

  if (diffHours === 0 && diffMinutes === 1) {
    const remainingSeconds = getNumberOfSeconds(timeDiff) - 59;
    return t("remainingSeconds", { remainingSeconds });
  }

  if (getNumberOfDays(timeDiff) !== 0) {
    return t("moreThan24hLeft");
  }

  return t("remainingLeft", { remainingHours, remainingMinutes });
});

const minutesToAdd = computed(() => {
  const reservation = scheduleStore.firstFutureReservation;
  const followingReservation = scheduleStore.futureReservations[1];

  let timeToLimit = 15;
  let timeToNextReservation = 15;

  if (followingReservation) {
    const timeDiff = getTimeDiff(followingReservation.startTime, reservation.endTime);
    timeToNextReservation = getNumberOfMinutes(timeDiff);
  }

  if (timeLimit.value) {
    const timeDiffToLimit = getTimeDiff(addMinutes(reservation.startTime, timeLimit.value), reservation.endTime);

    timeToLimit = getNumberOfMinutes(timeDiffToLimit);
  }

  return timeToLimit < timeToNextReservation ? timeToLimit : timeToNextReservation;
});

const addTime = computed(() => {
  if (minutesToAdd.value > 15) {
    return t("minutesToAdd.15minutes");
  }

  if (minutesToAdd.value < 1) {
    return t("minutesToAdd.none");
  }

  return t("minutesToAdd.lessThan15", { count: minutesToAdd.value });
});

watchEffect(() => {
  if (!firstFutureReservation.value || !profileStore.endtimeAudioReminderTime) return;
  const playFrom = subMinutes(firstFutureReservation.value.endTime, profileStore.endtimeAudioReminderTime);
  const playTo = subMinutes(firstFutureReservation.value.endTime, profileStore.endtimeAudioReminderTime - 1);
  if (scheduleStore.getTime >= playFrom && scheduleStore.getTime <= playTo) {
    playSound();
  }
});

watch(firstFutureReservation, () => {
  soundPlayed.value = false;
});

const onEndReservation = async () => {
  loggingService.info("InProgress.vue", `🏁 End now action on reservation with ID: ${firstFutureReservation.value.id}`);
  endNowModal.value = false;
  isEndReservationLoading.value = true;
  const { error } = await scheduleStore.endInProgressReservation();
  if (error) {
    showErrorModal.value = true;
    loggingService.error(
      "InProgress.vue",
      `❗️ Failed to end reservation with ID: ${firstFutureReservation.value.id} Error: ${error}`
    );
    errorMessage.value = "cannotEndReservation";
  }

  isEndReservationLoading.value = false;
};

const showAddTimeButton = computed(() => {
  return !profileStore.resource?.approvalQueue;
});

const minutesToNextReservation = computed(() => {
  let timeToNextReservation = +Infinity;
  const reservation = scheduleStore.firstFutureReservation;
  const followingReservation = scheduleStore.futureReservations[1];
  if (followingReservation) {
    const timeDiff = getTimeDiff(followingReservation.startTime, reservation.endTime);
    timeToNextReservation = getNumberOfMinutes(timeDiff);
  }
  return timeToNextReservation;
});

const onAddTime = () => {
  endNowModal.value = false;

  // Ignore clicks while a request is in flight
  if (addTimeRequestInFlight.value) {
    return;
  }

  if (minutesToAdd.value < 1) {
    errorMessage.value = "cannotAddTime";
    showErrorModal.value = true;
    return;
  }

  // Increment click counter for this burst (each click = +15 minutes)
  addTimeClickCount.value += 1;

  // Clear existing debounce timer if present
  if (addTimeTimer !== null) {
    clearTimeout(addTimeTimer);
  }

  isAddTimeLoading.value = true;

  // Start a new 500ms debounce timer
  addTimeTimer = window.setTimeout(async () => {
    const clicks = addTimeClickCount.value;
    addTimeClickCount.value = 0;
    addTimeTimer = null;

    // Compute total minutes based on clicks, respecting current max extend
    const maxExtend = minutesToNextReservation.value;
    const totalMinutes = Math.min(clicks * 15, maxExtend);
    if (totalMinutes < 1) {
      return;
    }

    addTimeRequestInFlight.value = true;

    loggingService.info(
      "InProgress.vue",
      `⌛ Add time action on reservation with ID: ${firstFutureReservation.value.id}, minutes: ${totalMinutes}`
    );

    // @ts-ignore addTime action accepts minutes as a parameter at runtime
    const { error } = await scheduleStore.addTime(totalMinutes);
    if (error) {
      showErrorModal.value = true;
      loggingService.error(
        "InProgress.vue",
        `❗️ Failed to add time to reservation with ID: ${firstFutureReservation.value.id} Error: ${error}`
      );
      errorMessage.value = "cannotAddTime";
    }

    isAddTimeLoading.value = false;
    addTimeRequestInFlight.value = false;
  }, 500);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "flex flex-col",
    onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('closeUpcoming')))
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.$t("resourceName", { resourceName: _unref(profileStore).resourceName })), 1),
      _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(reservationTitle)), 1),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(startTime)), 1),
        _createElementVNode("sup", null, _toDisplayString(_unref(pastDays)), 1),
        _hoisted_6,
        _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(endTime)), 1),
        _createElementVNode("sup", _hoisted_8, _toDisplayString(_unref(futureDays)), 1),
        _createElementVNode("span", _hoisted_9, " ・ " + _toDisplayString(_unref(remaining)), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createVNode(ProgressBar, { width: _unref(barProgress) }, null, 8, ["width"])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createVNode(BaseButton, {
        loading: isEndReservationLoading.value,
        disabled: isEndReservationLoading.value,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (endNowModal.value = true)),
        type: _unref(BUTTONTYPE).PRIMARY,
        class: "flex-grow"
      }, {
        default: _withCtx(() => [
          _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.$t("endNow")), 1)
        ]),
        _: 1
      }, 8, ["loading", "disabled", "type"]),
      (_unref(showAddTimeButton))
        ? (_openBlock(), _createBlock(BaseButton, {
            key: 0,
            loading: isAddTimeLoading.value,
            type: _unref(BUTTONTYPE).SECONDARY,
            class: _normalizeClass(_unref(addTimeButtonClass)),
            onClick: onAddTime
          }, {
            default: _withCtx(() => [
              _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(addTime)), 1)
            ]),
            _: 1
          }, 8, ["loading", "type", "class"]))
        : _createCommentVNode("", true)
    ]),
    (endNowModal.value)
      ? (_openBlock(), _createBlock(EndMeetingModal, {
          key: 0,
          onCancel: _cache[1] || (_cache[1] = ($event: any) => (endNowModal.value = false)),
          onSubmit: onEndReservation
        }))
      : _createCommentVNode("", true),
    (showErrorModal.value)
      ? (_openBlock(), _createBlock(ErrorModal, {
          key: 1,
          "error-message": errorMessage.value,
          onClose: _cache[2] || (_cache[2] = ($event: any) => (showErrorModal.value = false))
        }, null, 8, ["error-message"]))
      : _createCommentVNode("", true)
  ]))
}
}

})